import React, { useContext, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, IconButton, Icon } from '@material-ui/core';
import { NotificationsTable } from '../NotificationsTable';
import { LanguageString } from '../../Common';
import { useStudentNotificationsCtx, useUserNotificationsCtx } from '../../../contexts/NotificationsProvider';
import { useNotificationMarkAllDeleted } from '../../../hooks';
import { ErrorResponse } from '@apollo/client/link/error';
import { NotificationsDeleteDialog } from '../NotificationsDeleteDialog';
import { useDialogController } from '../../../hooks';
import { StudentContext } from '../../../contexts/StudentProvider';
import { UserContext } from '../../../contexts/UserProvider';

interface NotificationsDialogProps {
  currentUserContext: boolean;
  open: boolean;
  toggle: () => void;
}

export const NotificationsDialog: React.FC<NotificationsDialogProps> = ({ open, toggle, currentUserContext }) => {
  const student = useContext(StudentContext);
  const user = useContext(UserContext);
  const [markDeleted] = useNotificationMarkAllDeleted();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { controller: { toggle: toggleNotificationsDeleteDialog }, props: { open: notificationsDeleteDialogOpen }} = useDialogController(false);


  const { notificationIds: studentNotificationIds } = useStudentNotificationsCtx();
  const { notificationIds: userNotificationIds } = useUserNotificationsCtx();
  const deleteIds = useMemo(() => {
    return currentUserContext ? userNotificationIds : studentNotificationIds;
  }, [currentUserContext, userNotificationIds, studentNotificationIds]);

  const isErrorResponse = (res: unknown): res is ErrorResponse => (res as ErrorResponse).graphQLErrors != null;

  const handleDeleteAll = async () => {
    try {
      await markDeleted({ variables: { userId: (currentUserContext ? user.user.id : student?.student.id) ?? 0 }, refetchQueries: ['getUser', 'getUserNotifications_menu', 'getUserNotifications_roadmap'] });

    } catch (res: unknown) {
      if (!isErrorResponse(res)) return;

      const errors = res.graphQLErrors?.map(error => error.message);
      if (errors?.length && errorMessage !== errors[0]) setErrorMessage(errors[0]);
    }
  };

  return (
    <>
      <Dialog maxWidth="lg" fullWidth scroll="paper" open={open} onClose={toggle}>
        <DialogTitle>
          <Grid container direction="row" justify="space-between">
            <Grid item>
              <LanguageString groupName="GENERAL" resourceName="NOTIFICATIONS" defaultText="Notifications" />
            </Grid>
            <Grid item>
              <IconButton edge="start" color="inherit" onClick={toggle} aria-label="close">
                <Icon>close</Icon>
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent id='dialogBox' dividers>
          <NotificationsTable currentUserContext={currentUserContext} clickedNotification={toggle} scrollId='scrollableDivDialogue' deleteOnTop={true} />
        </DialogContent>
        <DialogActions>

          <Button onClick={toggleNotificationsDeleteDialog} disabled={deleteIds.length <= 0} color="primary" style={{ marginRight: '15px' }}>
            <LanguageString groupName="GENERAL" resourceName="DELETEALL" defaultText="Delete All" />
          </Button>
          <Button onClick={toggle} color="primary">
            <LanguageString groupName="GENERAL" resourceName="CANCEL" defaultText="Cancel" />
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationsDeleteDialog
        open={notificationsDeleteDialogOpen}
        toggle={toggleNotificationsDeleteDialog}
        deleteAll={handleDeleteAll}
      />
    </>
  );
};

export default NotificationsDialog;
